import React, { useEffect, useState, createRef, useLayoutEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { createTheme } from "@mui/material/styles";
import {
  Alert,
  Container,
  Grid,
  Button,
  CircularProgress,
  TextField,
  Checkbox,
} from "@mui/material";

import "./styles.css";
import { Header } from "../Header";
import { Dropzone } from "../Dropzone";
import {
  submitInferenceRequest,
  getTitleByYoutubeURL,
} from "../../services/api.service";
import { Input } from "@mui/base";

const theme = createTheme({
  palette: {
    primary: {
      main: "#8439C8",
    },
  },
});

const MEDIA_TYPE = {
  TEXT: "text",
  IMAGE: "image",
  AUDIO: "audio",
  VIDEO: "video",
};

const ALLOWED_FILE_EXTENSIONS = {
  txt: [MEDIA_TYPE.TEXT],
  png: [MEDIA_TYPE.IMAGE],
  mp3: [MEDIA_TYPE.AUDIO],
  mp4: [MEDIA_TYPE.AUDIO, MEDIA_TYPE.VIDEO],
  jpg: [MEDIA_TYPE.IMAGE],
  jpeg: [MEDIA_TYPE.IMAGE],
  wav: [MEDIA_TYPE.AUDIO],
  docx: [MEDIA_TYPE.TEXT],
  pdf: [MEDIA_TYPE.TEXT, MEDIA_TYPE.IMAGE],
};

export const CONTENT_TYPE = {
  txt: "text/plain",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  wav: "audio/wav",
  mp3: "audio/mpeg",
  mp4: "video/mp4",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pdf: "application/pdf",
};

function FocusTextField({ email, setEmail }) {
  const actionRef = createRef();
  const { user } = useAuth0();

  useLayoutEffect(() => {
    if (actionRef.current) {
      actionRef.current.focusVisible();
    }
  }, [actionRef]);

  return (
    <TextField
      label="Input your email..."
      value={email}
      id="outlined-basic"
      variant="outlined"
      sx={{
        margin: "8%",
        input: {
          color: "black",
          background: "white",
        },
      }}
      onChange={(event) => {
        setEmail(event.target.value);
      }}
    />
  );
}

function LoginButton() {
  const { loginWithRedirect, isAuthenticated } = useAuth0();

  return (
    !isAuthenticated && (
      <button className="login-button" onClick={() => loginWithRedirect()}>
        <span className="buttontext-login">Login</span>
      </button>
    )
  );
}

function LogoutButton() {
  const { logout, isAuthenticated } = useAuth0();
  const origin = `${window.location.protocol}//${window.location.hostname}${
    window.location.port ? ":" + window.location.port : ""
  }`;

  return (
    isAuthenticated && (
      <button
        className="logout-button"
        onClick={() =>
          logout({
            logoutParams: {
              returnTo: origin,
            },
          })
        }
      >
        <span className="button-text">Logout</span>
      </button>
    )
  );
}

function Profile() {
  const { user, isAuthenticated } = useAuth0();

  return (
    isAuthenticated && (
      <div className="profile">
        {user.name.split(" ")[0]}
        <br />
        {user.email}
      </div>
    )
  );
}

export function Topbar() {
  return (
    <div className="topbar">
      <div className="row">
        <img
          src="https://deepid-assets.s3.amazonaws.com/all_images/logo_deepid.png"
          alt="Logo"
          className="logo"
        />
        <div className="login">
          <Profile />
          <LoginButton />
          <LogoutButton />
        </div>
      </div>
    </div>
  );
}

function Footer() {
  return (
    <div className="footer">
      <div className="footer-center">
        <a href="https://www.deepmedia.ai/">
          <span className="footer-text">
            © 2023 DeepMedia.AI All Rights Reserved.
          </span>
        </a>
      </div>
    </div>
  );
}

function MediaOptions({ filename, checkedItems, setCheckedItems }) {
  const [disabledItems, setDisabledItems] = useState({
    [MEDIA_TYPE.TEXT]: false,
    [MEDIA_TYPE.IMAGE]: false,
    [MEDIA_TYPE.AUDIO]: false,
    [MEDIA_TYPE.VIDEO]: false,
  });

  useEffect(() => {
    let allowedExtensions = [];
    if (filename) {
      const extension = filename.slice(-3).toLowerCase();
      if (Object.keys(ALLOWED_FILE_EXTENSIONS).includes(extension))
        allowedExtensions = ALLOWED_FILE_EXTENSIONS[extension];
    }

    let newDisabledItems = {};
    for (const mediaType in disabledItems)
      newDisabledItems[mediaType] =
        allowedExtensions.length > 0 && !allowedExtensions.includes(mediaType);
    setDisabledItems(newDisabledItems);

    let newCheckedItems = {};
    for (const mediaType in checkedItems)
      newCheckedItems[mediaType] =
        !newDisabledItems[mediaType] && checkedItems[mediaType];
    setCheckedItems(newCheckedItems);
  }, [filename]);

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setCheckedItems((prevCheckedItems) => ({
      ...prevCheckedItems,
      [name]: checked,
    }));
  };

  return (
    <div className="checkbox-container">
      {Object.keys(checkedItems).map((key) => (
        <label key={key} className="checkbox-label">
          <Checkbox
            className="checkbox"
            sx={{
              color: "white",
            }}
            name={key}
            checked={checkedItems[key]}
            disabled={disabledItems[key]}
            onChange={handleCheckboxChange}
          />
          {key}
          <span className="checkmark"></span>
        </label>
      ))}
    </div>
  );
}

function VideoUploader() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [s3Filename, setS3Filename] = useState(null);
  const [webFilename, setWebFilename] = useState("");
  const [loading, setLoading] = useState(false);
  const [titleLoading, setTitleLoading] = useState(false);
  const [checkedItems, setCheckedItems] = useState({
    [MEDIA_TYPE.TEXT]: false,
    [MEDIA_TYPE.IMAGE]: false,
    [MEDIA_TYPE.AUDIO]: false,
    [MEDIA_TYPE.VIDEO]: false,
  });
  const { user, isAuthenticated } = useAuth0();

  const getSelectedMediaTypes = () => {
    return Object.keys(checkedItems)
      .map((key) => (checkedItems[key] ? key : null))
      .filter((m) => m !== null);
  };

  const getAndSetTitleOfYoutubeURL = async (url) => {
    setTitleLoading(true);

    const response = await getTitleByYoutubeURL(url);

    setTitleLoading(false);

    setTitle(response.data.title);
  };

  const submitRequestAndParseResults = async (s3Filename, webFilename) => {
    setLoading(true);
    const mediaTypes = Object.keys(checkedItems)
      .map((key) => (checkedItems[key] ? key : null))
      .filter((m) => m !== null);

    let finalEmail;
    if (isAuthenticated) {
      finalEmail = user.email;
    } else {
      finalEmail = email;
    }

    const response = await submitInferenceRequest(
      s3Filename,
      webFilename,
      title,
      finalEmail,
      mediaTypes
    );
    setLoading(false);

    if (response.status == 200 || response.status == 201) {
      navigate(isAuthenticated ? "/dashboard" : "/success");
    } else {
      navigate("/failure");
    }
  };

  useEffect(() => {
    if (webFilename && webFilename.length > 0) {
      getAndSetTitleOfYoutubeURL(webFilename);
    }
  }, [webFilename]);

  return (
    <Container>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Dropzone setFilename={setS3Filename} />
          <p style={{ textAlign: "center" }}> — or enter a YouTube URL — </p>
          <TextField
            variant="standard"
            fullWidth={true}
            value={webFilename}
            onChange={(event) => setWebFilename(event.target.value)}
            className="YouTube-input-field"
          />
          <MediaOptions
            filename={s3Filename ? s3Filename : webFilename}
            checkedItems={checkedItems}
            setCheckedItems={setCheckedItems}
            box={checkedItems}
          />
          <div className="email-submit-container">
            {!isAuthenticated && (
              <div className="form-email-container">
                <p className="form-email-label">Email:</p>
                <TextField
                  label="Insert your email here"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{ sx: { borderRadius: "8px", color: "#fff" } }}
                  InputLabelProps={{ sx: { root: { color: "#fff" } } }}
                />
              </div>
            )}
            <div className="uploadActions">
              {webFilename && webFilename.length > 0 && !titleLoading ? (
                <TextField
                  label="Insert the name of file here"
                  sx={{
                    paddingRight: "5%",
                    marginTop: "2%",
                  }}
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  InputProps={{ sx: { borderRadius: "8px", color: "#fff" } }}
                  InputLabelProps={{ sx: { root: { color: "#fff" } } }}
                />
              ) : null}
              <Button
                disabled={
                  (!isAuthenticated && email.length == 0) ||
                  !getSelectedMediaTypes().length ||
                  !(s3Filename || (webFilename && title)) | loading
                }
                onClick={() =>
                  submitRequestAndParseResults(s3Filename, webFilename)
                }
                sx={{
                  backgroundColor: "#f7f0f0",
                  borderRadius: "16px 0px 16px 0px",
                  padding: "0% 5%",
                  width: "10vw",
                  "&:hover": {
                    backgroundColor: "#ffffff",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress sx={{ margin: "5px" }} />
                ) : (
                  <p
                    style={{
                      color:
                        (!isAuthenticated && email.length == 0) ||
                        !getSelectedMediaTypes().length ||
                        !(s3Filename || (webFilename && title)) | loading
                          ? "#CCCCCC"
                          : "#8439FF",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    Submit
                  </p>
                )}
              </Button>
            </div>
          </div>
        </Grid>
      </Grid>
    </Container>
  );
}

function MainContainer() {
  const domain = "app.deepidentify.ai";
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: `https://${domain}/api`,
          },
        });
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      } catch (e) {
        console.error(e);
      }
    })();
  }, [isAuthenticated]);

  return (
    <div className="row">
      <div className="column height300">
        <h1>AI Generated Content Detection with DeepID</h1>
        <hr className="division" />
        <p>
          Use Generative AI detectors to make your business more secure and
          trustworthy.
        </p>
        <p>
          <button className="contact-button">
            <a href="https://www.deepmedia.ai/">
              <span className="buttontext-learn-more">Learn More</span>
            </a>
          </button>
        </p>
      </div>
      <div className="form-container">
        <VideoUploader />
      </div>
    </div>
  );
}

export function UpdatedPage() {
  return (
    <div className="background-image">
      <div className="App">
        <header>
          <Header />
        </header>
        <div>
          <div className="container">
            <MainContainer />
          </div>
          <Footer className="footer" />
        </div>
      </div>
    </div>
  );
}
